import { ElementCategoryID } from '@shared/models/element-categories.model';
import {
  AHU_VARIABLE_NAME,
  AirHandlingUnitPropertyName,
} from '@shared/templates/categories/air-handling-unit/air-handling-unit.model';
import { getAhuVariables } from '@shared/templates/categories/air-handling-unit/air-handling-unit';
import { EXPRESSION_VARIABLE_NAME } from '@shared/constants/expression-variables-names.constant';
import { ENERGY_VARIABLE_NAME } from '@shared/helpers/energy/energy-expression-variables.models';
import { getElementCategoryId } from '../../element-category.helpers';
import { projectGuards } from '../../guards/project-guards.helpers';
import { GLOBAL_VAR_PREFIX, NODE_ID } from '../node-ids.helpers';
import type { ComputedVariable } from './types';
import { postHeatingDemandNodeId } from './heat-demand-record';
import { ExpressionVariables } from '@shared/helpers/expression-variables.helpers';

const AHU_VARIABLES_NAME = 'ahu_variables';

/**
 * Computed variable that adds per-AHU nodes (e.g. FAN_ENERGY_CONSUMPTION) so energy child count can depend on them.
 * Nodes are added only for elements whose category is ElementCategoryID.AirHandlingUnit.
 * Depends on post_heating_demand (from heat demand record), so must run after heatDemandRecordVariable.
 */
export const ahuVariablesVariable: ComputedVariable = {
  name: AHU_VARIABLES_NAME,
  getDependencies: () => [],
  ensureNodes: (ctx) => {
    for (const element of ctx.elementsInScope) {
      if (!projectGuards.is.element(element)) continue;
      if (getElementCategoryId(element) !== ElementCategoryID.AirHandlingUnit)
        continue;
      const ahu = element;
      const ahuFanNodeId = `${GLOBAL_VAR_PREFIX}${AHU_VARIABLE_NAME.FAN_ENERGY_CONSUMPTION}::${ahu.id}`;
      if (ctx.descriptorMap.has(ahuFanNodeId)) continue;
      ctx.descriptorMap.set(ahuFanNodeId, {
        type: 'ahuVar',
        elementId: ahu.id,
        name: AHU_VARIABLE_NAME.FAN_ENERGY_CONSUMPTION,
      });
      const ahuPropNodeIds = [
        NODE_ID.property(ahu.id, AirHandlingUnitPropertyName.Airflow),
        NODE_ID.property(ahu.id, AirHandlingUnitPropertyName.SpecificFanPower),
        NODE_ID.property(ahu.id, AirHandlingUnitPropertyName.OperationHours),
      ];
      ctx.nodes.push({
        id: ahuFanNodeId,
        dependencies: [postHeatingDemandNodeId, ...ahuPropNodeIds],
        compute: (resolvedDeps: Record<string, unknown>) => {
          const n0 = ahuPropNodeIds[0];
          const n1 = ahuPropNodeIds[1];
          const n2 = ahuPropNodeIds[2];
          const variables: Record<string, unknown> = {
            [ENERGY_VARIABLE_NAME.POST_HEATING_DEMAND]:
              (resolvedDeps[postHeatingDemandNodeId] as number) ?? 0,
            [AirHandlingUnitPropertyName.Airflow]:
              (n0 ? (resolvedDeps[n0] as number) : 0) ?? 0,
            [AirHandlingUnitPropertyName.SpecificFanPower]:
              (n1 ? (resolvedDeps[n1] as number) : 0) ?? 0,
            [AirHandlingUnitPropertyName.OperationHours]:
              (n2 ? (resolvedDeps[n2] as number) : 0) ?? 0,
            [EXPRESSION_VARIABLE_NAME.ACTIVE_ELEMENTS_COUNT_BY_CATEGORY]: {
              [ElementCategoryID.AirHandlingUnit]: 1,
            },
          };
          const ahuVars = getAhuVariables(
            variables as ExpressionVariables,
            ahu,
          );
          return ahuVars[AHU_VARIABLE_NAME.FAN_ENERGY_CONSUMPTION];
        },
      });
    }
  },
  resolve: () => 0,
};
